class DynamicPrice {
  constructor() {
    this.basePrice = 0;
    this.compareAtPrice = 0;
    this.selectedOptions = new Map(); // Map of optionId -> price
    this.priceElement = null;
    this.compareAtElement = null;
    this.clickHandlerRegistered = false;
    this.isUpdating = false; // Prevent infinite loops
    this.init();
  }

  init() {
    // ALWAYS register click handlers first (before any early returns)
    this.registerClickHandlers();

    // Debug: Log all extra option cards and their variant IDs
    this.debugLogAllCards();

    // Bundle: track which option IDs are bundle-included (pre-selected, non-deselectable)
    this.bundleIncludedOptionIds = new Set();
    this.isBundleProduct = false;

    // Auto-select bundle items after init completes
    setTimeout(() => {
      this.initBundlePreselect();
    }, 200);
    
    // Get base prices from the price component - find the price wrapper by ID pattern
    // Try to find section ID from variant-selects or product-form
    let sectionId = null;
    const variantSelects = document.querySelector('variant-selects');
    if (variantSelects) {
      sectionId = variantSelects.dataset.section || variantSelects.dataset.originalSection;
    }
    if (!sectionId) {
      const productForm = document.querySelector('product-form');
      if (productForm) {
        sectionId = productForm.dataset.section;
      }
    }
    
    // Find price wrapper - ensure we only get one
    let priceWrapperEl = null;
    if (sectionId) {
      priceWrapperEl = document.querySelector(`#price-${sectionId}`);
    }
    if (!priceWrapperEl) {
      // Fallback: get first price wrapper
      priceWrapperEl = document.querySelector('.price-wrapper');
    }
    
    // Check for duplicates
    const allPriceWrappers = document.querySelectorAll('.price-wrapper');
    if (allPriceWrappers.length > 1) {
      console.warn('DynamicPrice: Multiple price wrappers found', allPriceWrappers.length);
    }
    
    const priceWrapper = priceWrapperEl?.querySelector('.price');
    
    if (!priceWrapper) {
      console.warn('DynamicPrice: Price wrapper not found', { sectionId, priceWrapperEl, allPriceWrappers: allPriceWrappers.length });
      return;
    }
    
    this.priceWrapper = priceWrapperEl;
    this.priceElement = priceWrapper;

    // Get base prices from data attributes
    this.basePrice = parseInt(priceWrapper.dataset.basePrice) || 0;
    this.compareAtPrice = parseInt(priceWrapper.dataset.compareAtPrice) || 0;

    // Find the current price element (either regular or sale)
    const regularPriceEl = priceWrapper.querySelector('.price__regular [data-price-element]');
    const salePriceEl = priceWrapper.querySelector('.price__sale [data-price-element]');
    
    // Use sale price if on sale, otherwise regular price
    this.priceDisplayElement = salePriceEl || regularPriceEl;
    
    // Get compare at price element
    this.compareAtElement = priceWrapper.querySelector('[data-compare-at-element]');

    // If prices weren't in data attributes, try to parse from text
    if (this.basePrice === 0 && this.priceDisplayElement) {
      const priceText = this.priceDisplayElement.textContent.trim();
      // Try to extract price from money format (e.g., "€1.499,00" or "1.499,00")
      const priceMatch = priceText.match(/[\d.]+/);
      if (priceMatch) {
        const priceStr = priceMatch[0].replace(/\./g, '');
        this.basePrice = parseInt(priceStr) * 100; // Convert to cents
      }
    }

    // Listen for price updates from Shopify's variant selector
    const priceWrapperForObserver = document.querySelector('.price-wrapper');
    if (priceWrapperForObserver) {
      const observer = new MutationObserver(() => {
        // Price HTML was updated, reinitialize
        setTimeout(() => {
          this.reinitialize();
        }, 100);
      });
      observer.observe(priceWrapperForObserver, { childList: true, subtree: true });
    }

    // Update prices on page load
    this.updatePrices();
  }

  debugLogAllCards() {
    const cards = document.querySelectorAll('.extra-option-card');
    console.log('=== EXTRA OPTIONS DEBUG ===');
    console.log(`Found ${cards.length} extra option cards:`);
    
    cards.forEach((card, index) => {
      const title = card.querySelector('.extra-option-card__title')?.textContent?.trim() || 'No title';
      const variantId = card.dataset.variantId || 'NO VARIANT ID - WILL NOT ADD TO CART';
      const optionId = card.dataset.optionId || 'No option ID';
      
      if (card.dataset.variantId) {
        console.log(`✅ Card ${index + 1}: "${title}" - Variant ID: ${variantId} (Will be added to cart)`);
      } else {
        console.warn(`❌ Card ${index + 1}: "${title}" - ${variantId}`);
      }
    });
    
    console.log('=== END DEBUG ===');
  }

  initBundlePreselect() {
    const preselectDataEl = document.getElementById('bundle-preselect-data');
    if (!preselectDataEl) return;

    try {
      const data = JSON.parse(preselectDataEl.textContent);
      if (!data.preselectVariantIds || !data.preselectVariantIds.length) return;

      console.log('Bundle preselect: found variant IDs to pre-select:', data.preselectVariantIds);
      this.isBundleProduct = true;
      this.preselectVariants(data.preselectVariantIds);
    } catch (e) {
      console.error('Error in bundle preselect:', e);
    }
  }

  preselectVariants(variantIds) {
    if (!variantIds || !variantIds.length) return;

    console.log('Pre-selecting bundle variants:', variantIds);
    const cards = document.querySelectorAll('.extra-option-card');

    cards.forEach(card => {
      const cardVariantId = card.dataset.variantId;
      if (!cardVariantId) return;

      const isPreselect = variantIds.some(
        id => id.toString() === cardVariantId.toString()
      );

      if (isPreselect) {
        const optionId = card.dataset.optionId;
        if (!optionId) return;

        // Mark as bundle-included
        this.bundleIncludedOptionIds.add(optionId);
        card.classList.add('is-bundle-included');

        // Auto-select if not already selected
        if (!this.selectedOptions.has(optionId)) {
          this.handleOptionToggle(card);
        }

        console.log('Bundle pre-selected:', optionId, 'variant:', cardVariantId);
      }
    });
  }

  registerClickHandlers() {
    // Only register once
    if (this.clickHandlerRegistered) {
      return;
    }
    
    // Listen for extra option selections - handle clicks anywhere on card
    // Exclude clicks on the CTA link that opens the expanded view
    document.addEventListener('click', (e) => {
      const card = e.target.closest('.extra-option-card');
      const ctaLink = e.target.closest('.extra-option-card__cta-link');
      
      // If clicking the CTA link (opens expanded view), don't toggle selection
      if (ctaLink) {
        return;
      }
      
      // If clicking anywhere else on the card, toggle selection
      if (card) {
        e.preventDefault();
        e.stopPropagation();
        this.handleOptionToggle(card);
      }
    });

    // Listen for variant changes (price might change)
    document.addEventListener('change', (e) => {
      if (e.target.name === 'id' || e.target.closest('[name="id"]') || e.target.closest('variant-radios') || e.target.closest('variant-selects')) {
        // Wait a bit for variant to update
        setTimeout(() => {
          this.reinitialize();
        }, 200);
      }
    });

    // Listen for variant radio changes
    document.addEventListener('click', (e) => {
      if (e.target.closest('variant-radios input[type="radio"]')) {
        setTimeout(() => {
          this.reinitialize();
        }, 200);
      }
    });
    
    this.clickHandlerRegistered = true;
  }

  reinitialize() {
    // Re-find price elements after HTML update
    let sectionId = null;
    const variantSelects = document.querySelector('variant-selects');
    if (variantSelects) {
      sectionId = variantSelects.dataset.section || variantSelects.dataset.originalSection;
    }
    if (!sectionId) {
      const productForm = document.querySelector('product-form');
      if (productForm) {
        sectionId = productForm.dataset.section;
      }
    }
    
    const priceWrapperEl = sectionId ? document.querySelector(`#price-${sectionId}`) : document.querySelector('.price-wrapper');
    const priceWrapper = priceWrapperEl?.querySelector('.price');
    
    if (!priceWrapper) {
      console.warn('DynamicPrice: Price wrapper not found during reinitialize', { sectionId });
      return;
    }

    this.priceWrapper = priceWrapperEl;
    this.priceElement = priceWrapper;

    // Get base prices from data attributes
    this.basePrice = parseInt(priceWrapper.dataset.basePrice) || 0;
    this.compareAtPrice = parseInt(priceWrapper.dataset.compareAtPrice) || 0;

    // Find the current price element (either regular or sale)
    const regularPriceEl = priceWrapper.querySelector('.price__regular [data-price-element]');
    const salePriceEl = priceWrapper.querySelector('.price__sale [data-price-element]');
    
    // Use sale price if on sale, otherwise regular price
    this.priceDisplayElement = salePriceEl || regularPriceEl;
    
    // Get compare at price element
    this.compareAtElement = priceWrapper.querySelector('[data-compare-at-element]');

    // If prices weren't in data attributes, try to parse from text
    if (this.basePrice === 0 && this.priceDisplayElement) {
      const priceText = this.priceDisplayElement.textContent.trim();
      // Try to extract price from money format (e.g., "€1.499,00" or "1.499,00")
      const priceMatch = priceText.match(/[\d.]+/);
      if (priceMatch) {
        const priceStr = priceMatch[0].replace(/\./g, '');
        this.basePrice = parseInt(priceStr) * 100; // Convert to cents
      }
    }

    // Update prices with current selections
    this.updatePrices();
  }

  handleOptionToggle(card) {
    const optionId = card.dataset.optionId;
    console.log('Toggle attempt:', {
      optionId,
      currentSelections: Array.from(this.selectedOptions.keys()),
      isInMap: this.selectedOptions.has(optionId)
    });

    // Debug: Log all data attributes on the card
    console.log('Card data attributes:', {
      optionId: card.dataset.optionId,
      variantId: card.dataset.variantId,
      parent: card.dataset.parent,
      expandedId: card.dataset.expandedId,
      allDatasets: JSON.stringify(card.dataset)
    });

    if (!optionId) {
      console.warn('DynamicPrice: Extra option card missing data-option-id');
      return;
    }

    // Prevent deselecting bundle-included items
    if (this.bundleIncludedOptionIds.has(optionId) && this.selectedOptions.has(optionId)) {
      console.log('Cannot deselect bundle-included item:', optionId);
      return;
    }
    
    const priceButton = card.querySelector('.extra-option-card__price-button');
    if (!priceButton) {
      console.warn('DynamicPrice: Extra option card missing price button');
      return;
    }
    
    const priceText = priceButton.querySelector('.extra-option-card__price-text');
    const checkIcon = priceButton.querySelector('.extra-option-card__check-icon');
    
    let priceValue = parseInt(priceButton.dataset.price) || 0;
    
    // If price seems to be in euros (less than 100), convert to cents
    if (priceValue > 0 && priceValue < 100) {
      priceValue = priceValue * 100;
    }
    
    const variantId = card.dataset.variantId || null;
    console.log('Variant ID from card:', variantId, '(type:', typeof variantId, ')');
    
    const isCurrentlySelected = this.selectedOptions.has(optionId);

    if (isCurrentlySelected) {
      // DESELECT: Remove option
      this.selectedOptions.delete(optionId);
      card.classList.remove('is-selected');
      
      // Update visuals - show price, hide check
      card.style.borderColor = '';
      card.style.borderWidth = '';
      card.style.boxShadow = '';
      priceButton.style.backgroundColor = '';
      if (priceText) {
        priceText.style.opacity = '1';
        priceText.style.visibility = 'visible';
      }
      if (checkIcon) {
        checkIcon.style.display = 'none';
      }
      
      console.log('Extra option DESELECTED:', { optionId, variantId });
      console.log('Current selectedOptions after deselect:', Array.from(this.selectedOptions.entries()));
    } else {
      // SELECT: Add option - include product title for cart display
      const title = card.querySelector('.extra-option-card__title')?.textContent?.trim() || '';
      this.selectedOptions.set(optionId, { price: priceValue, variantId: variantId, title: title });
      card.classList.add('is-selected');
      
      // Update visuals - hide price, show check (use button blue color)
      card.style.borderColor = 'rgb(var(--color-accent))';
      card.style.borderWidth = '3px';
      card.style.boxShadow = '0 4px 16px rgba(var(--color-accent), 0.3)';
      if (priceText) {
        priceText.style.opacity = '0';
        priceText.style.visibility = 'hidden';
      }
      if (checkIcon) {
        checkIcon.style.display = 'block';
      }
      
      console.log('Extra option SELECTED:', { optionId, variantId, price: priceValue });
      console.log('Current selectedOptions after select:', Array.from(this.selectedOptions.entries()));
    }

    this.updatePrices();
  }

  updateBasePrice() {
    // Get updated variant price
    const variantId = document.querySelector('[name="id"]')?.value;
    if (!variantId) return;

    // Try to get price from variant JSON
    const variantScript = document.querySelector('script[type="application/json"]');
    if (variantScript) {
      try {
        const variants = JSON.parse(variantScript.textContent);
        const variant = variants.find(v => v.id.toString() === variantId.toString());
        if (variant) {
          this.basePrice = variant.price || 0;
          this.compareAtPrice = variant.compare_at_price || 0;
          
          // Update data attributes on price wrapper
          if (this.priceElement) {
            this.priceElement.dataset.basePrice = this.basePrice;
            this.priceElement.dataset.compareAtPrice = this.compareAtPrice;
          }
          
          this.updatePrices();
        }
      } catch (e) {
        console.error('Error parsing variants:', e);
      }
    }
  }

  calculateTotal() {
    let totalExtraOptions = 0;
    this.selectedOptions.forEach((option, optionId) => {
      // Skip bundle-included items - their price is already in the bundle base price
      if (this.bundleIncludedOptionIds.has(optionId)) return;
      const price = typeof option === 'object' ? option.price : option;
      totalExtraOptions += price;
    });

    return {
      total: this.basePrice + totalExtraOptions,
      compareAtTotal: this.compareAtPrice > 0 ? this.compareAtPrice + totalExtraOptions : 0
    };
  }

  formatPrice(priceInCents) {
    // Use Shopify's formatMoney if available, otherwise format manually
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(priceInCents, window.money_format || '{{amount}}');
    }
    
    // Fallback: format as euros with dots as thousands separator
    const euros = Math.round(priceInCents / 100);
    const formatted = euros.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return formatted + ',-';
  }

  updatePrices() {
    // Prevent infinite loops from MutationObserver
    if (this.isUpdating) {
      return;
    }
    
    if (!this.priceDisplayElement) {
      return;
    }
    
    this.isUpdating = true;
    
    const totals = this.calculateTotal();

    // Update current price - preserve existing format if possible
    const originalText = this.priceDisplayElement.textContent.trim();
    // Try to preserve currency symbol and format
    const currencyMatch = originalText.match(/[^\d.,\s]+/);
    const currency = currencyMatch ? currencyMatch[0] : '';
    
    // Format the new price
    const formattedPrice = this.formatPrice(totals.total);
    
    // If we have a currency symbol, prepend it
    if (currency && !formattedPrice.includes(currency)) {
      this.priceDisplayElement.textContent = currency + ' ' + formattedPrice;
    } else {
      this.priceDisplayElement.textContent = formattedPrice;
    }

    // Update compare at price
    if (this.compareAtElement && totals.compareAtTotal > totals.total) {
      const originalCompareText = this.compareAtElement.textContent.trim();
      const currencyMatch = originalCompareText.match(/[^\d.,\s]+/);
      const currency = currencyMatch ? currencyMatch[0] : '';
      
      const formattedComparePrice = this.formatPrice(totals.compareAtTotal);
      
      if (currency && !formattedComparePrice.includes(currency)) {
        this.compareAtElement.textContent = currency + ' ' + formattedComparePrice;
      } else {
        this.compareAtElement.textContent = formattedComparePrice;
      }
      
      // Ensure compare at price is visible
      const compareWrapper = this.compareAtElement.closest('.price__compare');
      if (compareWrapper) {
        compareWrapper.style.display = '';
      }
    } else if (this.compareAtElement) {
      // Hide compare at price if not needed
      const compareWrapper = this.compareAtElement.closest('.price__compare');
      if (compareWrapper && totals.compareAtTotal <= totals.total) {
        compareWrapper.style.display = 'none';
      }
    }

    // Store totals for form submission (exclude bundle-included items)
    const extraOptionsTotal = Array.from(this.selectedOptions.entries()).reduce((sum, [id, option]) => {
      if (this.bundleIncludedOptionIds.has(id)) return sum;
      const price = typeof option === 'object' ? option.price : option;
      return sum + price;
    }, 0);

    document.dispatchEvent(new CustomEvent('dynamicPriceUpdate', {
      detail: {
        basePrice: this.basePrice,
        compareAtPrice: this.compareAtPrice,
        extraOptionsTotal: extraOptionsTotal,
        totalPrice: totals.total,
        compareAtTotal: totals.compareAtTotal,
        selectedOptions: Array.from(this.selectedOptions.entries())
      }
    }));
    
    // Reset the update flag after a short delay to allow DOM to settle
    setTimeout(() => {
      this.isUpdating = false;
    }, 50);
  }

  getSelectedOptions() {
    return Array.from(this.selectedOptions.entries()).map(([id, data]) => {
      const isBundleIncluded = this.bundleIncludedOptionIds.has(id);
      if (typeof data === 'object') {
        return {
          id,
          price: data.price,
          variantId: data.variantId,
          title: data.title || '',
          bundleIncluded: isBundleIncluded
        };
      }
      return {
        id,
        price: data,
        title: '',
        bundleIncluded: isBundleIncluded
      };
    });
  }
}

// Initialize on page load
function initDynamicPrice() {
  if (window.dynamicPrice) {
    // Reinitialize existing instance
    window.dynamicPrice.reinitialize();
  } else {
    window.dynamicPrice = new DynamicPrice();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDynamicPrice);
} else {
  // DOM already loaded
  initDynamicPrice();
}

// Re-initialize on section load
document.addEventListener('shopify:section:load', () => {
  initDynamicPrice();
});

